import { inject } from '@angular/core';

import { AttributeDataService } from '../../services/attribute-data.service';
import { ComponentsService } from '../../services/components.service';
import { TemplateEditorUtilsService } from '../../services/template-editor-utils.service';
import {
  LayoutAiComponent,
  LayoutAiShowOptions,
} from '../layout-ai/layout-ai.component';
import { GeneratedLayoutData } from '../layout-ai/services/layout-ai-api.service';

/**
 * Layout selection and AI layout behavior shared by the rise-layout-* editors.
 * Subclasses load and persist their own settings via {@link save}.
 */
export abstract class LayoutEditorBase {
  // Rise admins only, and not in production or beta yet
  readonly AI_LAYOUT_ROLE = 'sa !PROD !BETA';

  public componentId: string;
  public layout: string;
  protected componentLayout: any;

  private previousCustomLayout: any = null;
  private readonly layoutComponentsService = inject(ComponentsService);
  private readonly layoutAttributeDataService = inject(AttributeDataService);
  private readonly layoutEditorUtils = inject(TemplateEditorUtilsService);

  /** The rise-layout-* component the editor configures. */
  protected abstract readonly layoutComponent: string;

  protected abstract save(): void;

  get isCustomLayout(): boolean {
    return this.layout === ComponentsService.LAYOUT_TYPE_CUSTOM;
  }

  protected loadLayoutSelection(defaultLayoutId: string): void {
    this.previousCustomLayout = null;
    this.layout = ComponentsService.getLayoutSelection(
      this.componentLayout,
      defaultLayoutId,
    );
  }

  protected applyLayoutSelection(): void {
    if (
      !this.isCustomLayout &&
      this.componentLayout?.type === ComponentsService.LAYOUT_TYPE_CUSTOM
    ) {
      this.previousCustomLayout = this.componentLayout;
    }

    this.componentLayout = ComponentsService.applyLayoutSelection(
      this.componentLayout,
      this.layout,
    );
  }

  onLayoutGenerated(
    layoutHtml: string,
    usedAttributes?: string[],
    attributes?: Record<string, unknown>,
  ): void {
    this.componentLayout = ComponentsService.buildCustomLayout(
      layoutHtml,
      this.layoutComponent,
      usedAttributes,
    );
    this.layout = ComponentsService.LAYOUT_TYPE_CUSTOM;
    // Editor settings are named after the layout attributes they save.
    Object.assign(this, attributes);
    this.save();
  }

  usesAttribute(attribute: string): boolean {
    return ComponentsService.usesLayoutAttribute(
      this.componentLayout,
      attribute,
    );
  }

  openLayoutAi(): void {
    if (!this.isCustomLayout && this.previousCustomLayout) {
      this.onLayoutGenerated(
        this.previousCustomLayout.layoutHtml,
        this.previousCustomLayout.usedAttributes,
      );
      return;
    }

    this.showLayoutAi();
  }

  editLayoutAi(): void {
    this.showLayoutAi(this.componentLayout?.layoutHtml);
  }

  private showLayoutAi(layoutHtml?: string): void {
    const componentId = this.componentId;

    this.layoutComponentsService.editComponent(
      { type: LayoutAiComponent.TYPE },
      {
        component: this.layoutComponent.replace(/^rise-layout-/, ''),
        componentId,
        layoutHtml,
        onGenerated: (layout) => this.applyGeneratedLayout(componentId, layout),
      } satisfies LayoutAiShowOptions,
    );
    // The page has no component of its own, so keep this one outlined.
    this.layoutComponentsService.highlightComponent(componentId);
  }

  // The generation can finish after the editor moved on to another component.
  private applyGeneratedLayout(
    componentId: string,
    layout: GeneratedLayoutData,
  ): void {
    if (componentId === this.componentId) {
      this.onLayoutGenerated(
        layout.html,
        layout.usedAttributes,
        layout.attributes,
      );
      return;
    }

    const currentLayout = this.layoutEditorUtils.safeParseJson(
      this.layoutAttributeDataService.getAvailableAttributeData(
        componentId,
        'layout',
      ),
      {},
    );

    this.layoutAttributeDataService.setAttributeData(componentId, 'layout', {
      ...ComponentsService.buildCustomLayout(
        layout.html,
        this.layoutComponent,
        layout.usedAttributes,
      ),
      attributes: { ...currentLayout.attributes, ...layout.attributes },
    });
  }
}
